import React from "react";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";

/*
 * Small presentational helpers shared by the dashboard panels: the panel
 * shell, number formatters that never print NaN, and the tone palette.
 */

export const clamp = (value, min = 0, max = 100) => Math.max(min, Math.min(max, value));

export function fmtPrice(value, decimals = 0) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export function fmtCompact(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "—";
  const n = Math.abs(Number(value));
  const sign = Number(value) < 0 ? "−" : "";
  if (n >= 1e7) return `${sign}${(n / 1e7).toFixed(1)}Cr`;
  if (n >= 1e5) return `${sign}${(n / 1e5).toFixed(1)}L`;
  if (n >= 1e3) return `${sign}${(n / 1e3).toFixed(1)}K`;
  return `${sign}${Math.round(n)}`;
}

export function signed(value, suffix = "%", decimals = 2) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const n = Number(value);
  return `${n > 0 ? "+" : ""}${n.toFixed(decimals)}${suffix}`;
}

export const toneClasses = {
  emerald: { text: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30", bar: "bg-emerald-400" },
  red: { text: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/30", bar: "bg-rose-400" },
  rose: { text: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/30", bar: "bg-rose-400" },
  amber: { text: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/30", bar: "bg-amber-400" },
  cyan: { text: "text-cyan-300", bg: "bg-cyan-500/10", border: "border-cyan-500/30", bar: "bg-cyan-400" },
  violet: { text: "text-violet-300", bg: "bg-violet-500/10", border: "border-violet-500/30", bar: "bg-violet-400" },
  slate: { text: "text-slate-300", bg: "bg-slate-500/10", border: "border-slate-600/40", bar: "bg-slate-400" },
};

/* OI/PCR change indicator: ▲ added (green) / ▼ reduced (red) with magnitude. */
export function OiArrow({ value, fmt = fmtCompact, label = "" }) {
  if (value == null || Number.isNaN(Number(value))) return null;
  const n = Number(value);
  if (n === 0) return <p className="text-[10px] text-slate-600">▬ 0{label}</p>;
  const up = n > 0;
  return (
    <p className={`text-[10px] font-bold ${up ? "text-emerald-400" : "text-rose-400"}`}>
      {up ? "▲" : "▼"} {fmt(Math.abs(n))}{label}
    </p>
  );
}

export function TinyTrend({ value, inverse = false }) {
  if (value == null || Number.isNaN(Number(value))) {
    return <span className="font-semibold text-slate-600">—</span>;
  }
  const n = Number(value || 0);
  const positive = inverse ? n <= 0 : n >= 0;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${positive ? "text-emerald-400" : "text-rose-400"}`}>
      {n >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {signed(n)}
    </span>
  );
}

export function EmptyData({ label = "Waiting for live option-chain data" }) {
  return <div className="flex min-h-32 items-center justify-center px-5 text-center text-xs text-slate-600">{label}</div>;
}

export function Panel({ title, eyebrow, icon: Icon, action, children, className = "", testId }) {
  return (
    <section data-testid={testId} className={`overflow-hidden rounded-2xl border border-line bg-surface shadow-md ${className}`}>
      <div className="flex items-center justify-between gap-3 border-b border-line bg-surface-2/60 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          {Icon
            ? <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-cyan-500/25 bg-cyan-500/[.08] text-cyan-300"><Icon className="h-3.5 w-3.5" /></span>
            : <span className="h-3.5 w-1 shrink-0 rounded-full bg-slate-600" />}
          <div className="min-w-0">
            {eyebrow && <p className="truncate text-[10px] font-bold uppercase tracking-eyebrow text-low">{eyebrow}</p>}
            <h2 className="truncate text-xs font-bold uppercase tracking-label text-hi">{title}</h2>
          </div>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}
